import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoModel } from '../types';
import { listVideoModels, createVideoModel, updateVideoModel, deleteVideoModel } from '../api';
import Breadcrumb from '../components/Breadcrumb';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import {
  DEFAULT_MAX_DURATION_SEC,
  DEFAULT_MIN_DURATION_SEC,
  MAX_CONFIGURABLE_DURATION_SEC,
} from '../utils/videoLimits';

const emptyForm = (): Partial<VideoModel> => ({
  platform: '',
  model: '',
  displayName: '',
  pricePerSec: 0,
  pricePerSecWithVideo: 0,
  creditsPerSec: 0,
  creditsPerSecWithVideo: 0,
  minDurationSec: 0,
  maxDurationSec: 0,
  maxRefImages: 0,
  maxRefVideos: 0,
  maxRefAudios: 0,
  maxRefTotal: 0,
  isActive: true,
});

const VideoModelConfig: React.FC = () => {
  const { t } = useTranslation('config');
  const toast = useToast();
  const [models, setModels] = useState<VideoModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<VideoModel | null>(null);
  const [form, setForm] = useState<Partial<VideoModel>>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<VideoModel | null>(null);

  const load = () => {
    setLoading(true);
    listVideoModels().then(setModels).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openEdit = (m: VideoModel) => {
    setEditing(m);
    setForm({
      platform: m.platform,
      model: m.model,
      displayName: m.displayName,
      pricePerSec: m.pricePerSec,
      pricePerSecWithVideo: m.pricePerSecWithVideo,
      creditsPerSec: m.creditsPerSec,
      creditsPerSecWithVideo: m.creditsPerSecWithVideo,
      // 这 6 个字段必须回填。漏掉的话：handleSave 提交整个 form，缺失字段被
      // JSON.stringify 丢掉 → 后端 *int 收到 nil → 本次不改（不会立刻清零），
      // 但编辑框会把已配置的限制显示成 0 误导管理员；而管理员只要碰一下那个
      // 数字框，就会发出显式 0，那一刻才真的把限制清掉。
      minDurationSec: m.minDurationSec,
      maxDurationSec: m.maxDurationSec,
      maxRefImages: m.maxRefImages,
      maxRefVideos: m.maxRefVideos,
      maxRefAudios: m.maxRefAudios,
      maxRefTotal: m.maxRefTotal,
      isActive: m.isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.platform || !form.model) {
      toast.error(t('videoModel.platformModelRequired'));
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateVideoModel(editing.id, form);
        toast.success(t('videoModel.updateSuccess'));
      } else {
        await createVideoModel(form);
        toast.success(t('videoModel.createSuccess'));
      }
      setShowModal(false);
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m: VideoModel) => {
    try {
      await deleteVideoModel(m.id);
      toast.success(t('videoModel.deleted'));
      load();
    } catch (e: any) {
      toast.error(e.message);
    }
    setDeleteConfirm(null);
  };

  // 未配置项必须显式渲染成「不限」：省略会让「只配了图片上限」被误读成「全部配置」。
  const formatLimits = (m: VideoModel): string => {
    const minConfigured = m.minDurationSec > 0;
    const maxConfigured = m.maxDurationSec > 0;
    const min = minConfigured ? m.minDurationSec : DEFAULT_MIN_DURATION_SEC;
    const max = maxConfigured ? m.maxDurationSec : DEFAULT_MAX_DURATION_SEC;
    // 回退值必须可辨识：单边配置时（如 min=5 / max=0）若只渲染 `5-15s`，管理员
    // 看不出 15 是默认回退而非自己配的——与「未配置项不得省略」是同一类误读。
    const durationSuffix = (() => {
      if (minConfigured && maxConfigured) return '';
      if (!minConfigured && !maxConfigured) return `(${t('videoModel.limitsDefaultSuffix')})`;
      return `(${t(minConfigured ? 'videoModel.limitsMaxDefaultSuffix' : 'videoModel.limitsMinDefaultSuffix')})`;
    })();
    const unlimited = t('videoModel.limitsUnlimited');
    const counts = [
      `${t('videoModel.limitsImages')}${m.maxRefImages > 0 ? m.maxRefImages : unlimited}`,
      `${t('videoModel.limitsVideos')}${m.maxRefVideos > 0 ? m.maxRefVideos : unlimited}`,
      `${t('videoModel.limitsAudios')}${m.maxRefAudios > 0 ? m.maxRefAudios : unlimited}`,
      `${t('videoModel.limitsTotal')}${m.maxRefTotal > 0 ? m.maxRefTotal : unlimited}`,
    ].join('/');
    return `${min}-${max}s${durationSuffix} · ${counts}`;
  };

  const hasNoCountLimits = (m: VideoModel): boolean =>
    m.maxRefImages <= 0 && m.maxRefVideos <= 0 && m.maxRefAudios <= 0 && m.maxRefTotal <= 0;

  // group by platform
  const platforms = Array.from(new Set(models.map(m => m.platform)));

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-3)' }}>{t('videoModel.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container">
      <Breadcrumb items={[{ label: t('videoModel.breadcrumbHome'), to: '/projects' }, { label: t('videoModel.breadcrumbTitle') }]} />
      <div className="page-header">
        <h1 className="page-title">{t('videoModel.pageTitle')}</h1>
      </div>

      {platforms.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-3)' }}>
          {t('videoModel.noModels')}
        </div>
      ) : (
        platforms.map(platform => (
          <div key={platform} className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem' }}>{platform}</h3>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: '900px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>{t('videoModel.modelColumn')}</th>
                  <th style={thStyle}>{t('videoModel.displayNameColumn')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('videoModel.pricePerSec')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('videoModel.pricePerSecWithVideo')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('videoModel.creditsPerSec')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('videoModel.creditsPerSecWithVideo')}</th>
                  <th style={thStyle}>{t('videoModel.limitsColumn')}</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>{t('videoModel.statusColumn')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('videoModel.actionsColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {models.filter(m => m.platform === platform).map(m => (
                  <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ ...tdStyle, whiteSpace: 'normal', wordBreak: 'break-all' }}><code style={{ fontSize: '0.8rem' }}>{m.model}</code></td>
                    <td style={tdStyle}>{m.displayName || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>¥{m.pricePerSec.toFixed(4)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>¥{m.pricePerSecWithVideo.toFixed(4)}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{m.creditsPerSec || '-'}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{m.creditsPerSecWithVideo || '-'}</td>
                    <td style={tdStyle}>
                      {formatLimits(m)}
                      {m.isActive && hasNoCountLimits(m) && (
                        <span
                          className="status-badge"
                          style={{ fontSize: '0.75rem', marginLeft: '0.4rem', color: 'var(--warning)', background: 'color-mix(in srgb, var(--warning) 18%, transparent)' }}
                        >
                          {t('videoModel.limitsUnset')}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span className={`status-badge ${m.isActive ? 'status-completed' : 'status-failed'}`} style={{ fontSize: '0.75rem' }}>
                        {m.isActive ? t('videoModel.enabled') : t('videoModel.stopped')}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', marginRight: '0.25rem' }} onClick={() => openEdit(m)}>{t('videoModel.edit')}</button>
                      <button className="btn btn-ghost" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', color: 'var(--error)' }} onClick={() => setDeleteConfirm(m)}>{t('videoModel.delete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        ))
      )}

      <Modal open={showModal} title={editing ? t('videoModel.editModel') : t('videoModel.addModel')} onClose={() => setShowModal(false)} width="420px">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label style={labelStyle}>
              {t('videoModel.platformLabel')}
              <input className="input" value={form.platform || ''} onChange={e => setForm(f => ({ ...f, platform: e.target.value }))} placeholder={t('videoModel.platformPlaceholder')} disabled={!!editing} style={editing ? { opacity: 0.6, cursor: 'not-allowed' } : undefined} />
            </label>
            <label style={labelStyle}>
              {t('videoModel.modelNameLabel')}
              <input className="input" value={form.model || ''} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} placeholder={t('videoModel.modelNamePlaceholder')} disabled={!!editing} style={editing ? { opacity: 0.6, cursor: 'not-allowed' } : undefined} />
            </label>
            <label style={labelStyle}>
              {t('videoModel.displayNameLabel')}
              <input className="input" value={form.displayName || ''} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} placeholder={t('videoModel.displayNamePlaceholder')} />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label style={labelStyle}>
                {t('videoModel.pricePerSecLabel')}
                <input className="input" type="number" step="0.0001" value={form.pricePerSec ?? 0} onChange={e => setForm(f => ({ ...f, pricePerSec: parseFloat(e.target.value) || 0 }))} />
              </label>
              <label style={labelStyle}>
                {t('videoModel.pricePerSecWithVideoLabel')}
                <input className="input" type="number" step="0.0001" value={form.pricePerSecWithVideo ?? 0} onChange={e => setForm(f => ({ ...f, pricePerSecWithVideo: parseFloat(e.target.value) || 0 }))} />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label style={{ ...labelStyle, color: 'var(--warning)' }}>
                {t('videoModel.creditsPerSecLabel')}
                <input className="input" type="number" step="1" min="0" value={form.creditsPerSec ?? 0} onChange={e => setForm(f => ({ ...f, creditsPerSec: parseInt(e.target.value) || 0 }))} />
              </label>
              <label style={{ ...labelStyle, color: 'var(--warning)' }}>
                {t('videoModel.creditsPerSecWithVideoLabel')}
                <input className="input" type="number" step="1" min="0" value={form.creditsPerSecWithVideo ?? 0} onChange={e => setForm(f => ({ ...f, creditsPerSecWithVideo: parseInt(e.target.value) || 0 }))} />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label style={labelStyle}>
                {t('videoModel.minDurationSecLabel')}
                <input className="input" type="number" step="1" min="0" value={form.minDurationSec ?? 0} onChange={e => setForm(f => ({ ...f, minDurationSec: parseInt(e.target.value) || 0 }))} />
              </label>
              <label style={labelStyle}>
                {t('videoModel.maxDurationSecLabel')}
                <input className="input" type="number" step="1" min="0" value={form.maxDurationSec ?? 0} onChange={e => setForm(f => ({ ...f, maxDurationSec: parseInt(e.target.value) || 0 }))} />
              </label>
            </div>
            <small style={hintStyle}>
              {t('videoModel.durationHint', {
                min: DEFAULT_MIN_DURATION_SEC,
                max: DEFAULT_MAX_DURATION_SEC,
                ceiling: MAX_CONFIGURABLE_DURATION_SEC,
              })}
            </small>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label style={labelStyle}>
                {t('videoModel.maxRefImagesLabel')}
                <input className="input" type="number" step="1" min="0" value={form.maxRefImages ?? 0} onChange={e => setForm(f => ({ ...f, maxRefImages: parseInt(e.target.value) || 0 }))} />
              </label>
              <label style={labelStyle}>
                {t('videoModel.maxRefVideosLabel')}
                <input className="input" type="number" step="1" min="0" value={form.maxRefVideos ?? 0} onChange={e => setForm(f => ({ ...f, maxRefVideos: parseInt(e.target.value) || 0 }))} />
              </label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label style={labelStyle}>
                {t('videoModel.maxRefAudiosLabel')}
                <input className="input" type="number" step="1" min="0" value={form.maxRefAudios ?? 0} onChange={e => setForm(f => ({ ...f, maxRefAudios: parseInt(e.target.value) || 0 }))} />
              </label>
              <label style={labelStyle}>
                {t('videoModel.maxRefTotalLabel')}
                <input className="input" type="number" step="1" min="0" value={form.maxRefTotal ?? 0} onChange={e => setForm(f => ({ ...f, maxRefTotal: parseInt(e.target.value) || 0 }))} />
              </label>
            </div>
            <small style={hintStyle}>{t('videoModel.countHint')}</small>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
              <input type="checkbox" checked={form.isActive ?? true} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />
              {t('videoModel.enableCheckbox')}
            </label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>{t('videoModel.cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? t('videoModel.saving') : t('videoModel.save')}</button>
            </div>
          </div>
        </Modal>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={t('videoModel.confirmDeleteTitle')}
        message={t('videoModel.confirmDeleteMessage', { name: deleteConfirm?.displayName || deleteConfirm?.model || '' })}
        onConfirm={() => deleteConfirm && handleDelete(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  fontWeight: 600,
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.6rem 0.75rem',
  whiteSpace: 'nowrap',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  fontSize: '0.85rem',
  color: 'var(--text-2)',
};

// 「0 = 不限 / 用默认」这类语义提示不能用 placeholder：输入框恒有字面量 0，
// placeholder 永远不渲染，管理员看不到解释。
const hintStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--text-3, var(--text-2))',
  marginTop: '-0.35rem',
};

export default VideoModelConfig;
